import { useEffect, useRef, useState, type MouseEventHandler } from 'react';
import { availability } from '../availability';
import { INVITE_CODE_DIALOG_ID } from './InviteCodeDialog';

const navigationLinks = [
  ['Product', '#product'],
  ['Trust', '#trust'],
  ['FAQ', '#faq'],
] as const;

type MobileNavProps = {
  inviteDialogOpen: boolean;
  onInviteRequest: MouseEventHandler<HTMLButtonElement>;
};

export function MobileNav({ inviteDialogOpen, onInviteRequest }: MobileNavProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);
  const inviteDialogOpenRef = useRef(inviteDialogOpen);

  useEffect(() => {
    inviteDialogOpenRef.current = inviteDialogOpen;
  }, [inviteDialogOpen]);

  useEffect(() => {
    if (!open) {
      if (wasOpenRef.current) buttonRef.current?.focus({ preventScroll: true });
      wasOpenRef.current = false;
      return;
    }

    wasOpenRef.current = true;
    const previousOverflow = document.body.style.overflow;
    const focusable = () => [
      buttonRef.current,
      ...Array.from(
        overlayRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [],
      ),
    ].filter((element): element is HTMLElement => Boolean(element));

    document.body.style.overflow = 'hidden';
    overlayRef.current?.querySelector<HTMLElement>('a[href]')?.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (inviteDialogOpenRef.current) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        return;
      }

      if (event.key !== 'Tab') return;
      const elements = focusable();
      const first = elements[0];
      const last = elements.at(-1);

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div
      className="mobile-nav"
      role={open && !inviteDialogOpen ? 'dialog' : undefined}
      aria-modal={open && !inviteDialogOpen ? 'true' : undefined}
      aria-label={open && !inviteDialogOpen ? 'Site navigation' : undefined}
    >
      <button
        ref={buttonRef}
        className="mobile-nav-toggle"
        type="button"
        aria-label={open ? 'Close navigation' : 'Open navigation'}
        aria-expanded={open}
        aria-controls="mobile-navigation"
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true" />
        <span aria-hidden="true" />
      </button>
      {open ? (
        <div
          ref={overlayRef}
          className="mobile-nav-overlay"
          id="mobile-navigation"
          aria-hidden={inviteDialogOpen ? 'true' : undefined}
          inert={inviteDialogOpen ? true : undefined}
        >
          <nav aria-label="Mobile navigation">
            {navigationLinks.map(([label, href], index) => (
              <a href={href} key={href} onClick={close}>
                <span aria-hidden="true">0{index + 1}</span>
                {label}
              </a>
            ))}
          </nav>
          <div className="mobile-nav-action">
            <p>Private testing · iPhone first</p>
            <button
              type="button"
              data-invite-cta="mobile-drawer"
              aria-haspopup="dialog"
              aria-controls={INVITE_CODE_DIALOG_ID}
              onClick={onInviteRequest}
            >
              {availability.primaryLabel}<span aria-hidden="true">↗</span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
